import { FC, PropsWithChildren, useState } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';

import { OfferPropertiesText } from './OfferPropertiesText';
import { ScalePreview } from './ScalePreview.tsx';
import { useCart } from '../../context/cart-context';
import { Routes } from '../../models/routes';
import { OfferProperty } from '../../types';
import { Button } from '../ui/Button';
import { Image } from '../ui/Image';
import { Input } from '../ui/Input';
import { ScrollArea } from '../ui/ScrollArea';
import {
    Sheet,
    SheetTrigger,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '../ui/Sheet';

type PageProps = {
    offer_properties: OfferProperty[];
};

export const CartSheet: FC<PropsWithChildren> = ({ children }) => {
    const { offer_properties } = usePage<PageProps>().props;
    const { cartItems, removeFromCart, updateQuantity, clearCart, totalPrice } =
        useCart();
    const [open, setOpen] = useState(false);

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>{children}</SheetTrigger>
            <SheetContent className="flex w-full flex-col sm:max-w-lg">
                <SheetHeader className="flex flex-row items-center justify-between">
                    <SheetTitle className="text-2xl font-bold">
                        Кошик
                    </SheetTitle>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0 sm:hidden"
                        onClick={() => setOpen(false)}
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Закрити кошик</span>
                    </Button>
                </SheetHeader>
                {cartItems.length > 0 ? (
                    <>
                        <ScrollArea className="-mr-6 flex-grow pr-4">
                            <div className="space-y-4 py-4">
                                {cartItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex items-center space-x-4 py-2"
                                    >
                                        <ScalePreview triggerClassName="relative h-16 w-12 flex-shrink-0 overflow-hidden rounded-md">
                                            <Image
                                                src={item.image}
                                                alt={item.imageAlt || item.name}
                                                className="h-full w-full object-cover"
                                            />
                                        </ScalePreview>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="line-clamp-2 text-sm font-medium">
                                                {item.name}
                                            </h3>
                                            <OfferPropertiesText
                                                propertySet={item.propertySet}
                                                legacyCondition={item.condition}
                                                offerProperties={
                                                    offer_properties ?? []
                                                }
                                                productTypeId={
                                                    item.productTypeId
                                                }
                                                className="mt-1"
                                                dataTestId={`cart-offer-properties-${item.id}`}
                                            />

                                            <div className="flex gap-2">
                                                <div className="mt-2 flex gap-2">
                                                    <p className="text-sm font-medium">
                                                        ₴
                                                        {(
                                                            item.price *
                                                            item.quantity
                                                        ).toFixed(2)}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        (₴
                                                        {item.price.toFixed(
                                                            2,
                                                        )}{' '}
                                                        за шт.)
                                                    </p>
                                                </div>
                                                <div className="mt-2 flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-6 w-6 bg-transparent"
                                                        onClick={() =>
                                                            updateQuantity(
                                                                item.id,
                                                                item.quantity -
                                                                    1,
                                                            )
                                                        }
                                                        disabled={
                                                            item.quantity <= 1
                                                        }
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </Button>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) =>
                                                            updateQuantity(
                                                                item.id,
                                                                Number.parseInt(
                                                                    e.target
                                                                        .value,
                                                                ) || 1,
                                                            )
                                                        }
                                                        className="h-6 w-12 px-1 text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-6 w-6 bg-transparent"
                                                        onClick={() =>
                                                            updateQuantity(
                                                                item.id,
                                                                item.quantity +
                                                                    1,
                                                            )
                                                        }
                                                        disabled={
                                                            item.quantity >=
                                                            item.maxQuantity
                                                        }
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 flex-shrink-0"
                                            onClick={() =>
                                                removeFromCart(item.id)
                                            }
                                        >
                                            <X className="h-4 w-4" />
                                            <span className="sr-only">
                                                Видалити товар
                                            </span>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                        {cartItems.length > 0 && (
                            <div className="space-y-4 border-t pt-4">
                                <div className="flex justify-between text-base font-medium">
                                    <p>Загальна сума</p>
                                    <p>₴{totalPrice.toFixed(2)}</p>
                                </div>
                                <div className="space-y-2">
                                    <Button className="w-full">
                                        <Link
                                            href={Routes.Checkout}
                                            className="w-full"
                                        >
                                            Оформити замовлення
                                        </Link>
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="w-full bg-transparent"
                                        onClick={clearCart}
                                    >
                                        Очистити кошик
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center text-center">
                        <ShoppingCart className="mb-4 h-24 w-24 text-gray-300" />
                        <h3 className="text-xl font-semibold">
                            Ваш кошик порожній
                        </h3>
                        <p className="mt-2 text-muted-foreground">
                            Схоже, ви ще нічого не додали.
                        </p>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
